// The shell: navigation rail or tab bar, the routed view, toasts and the offline notice.
import { useEffect, useState } from 'preact/hooks';
import { useRoute, match, setRedirects } from './lib/router.js';
import { useStore } from './lib/store.js';
import { Icon } from './ui/icons.jsx';
import { Toasts, lazy, Avatar } from './ui/kit.jsx';
import { useMyArmy } from './play/my-army.js';

const Home = lazy(() => import('./views/Home.jsx'), 'Home');
const PlayComputer = lazy(() => import('./views/PlayComputer.jsx'), 'PlayComputer');
// The Lab: the super account's showcase of everything the game can make ("Not found" for everyone else).
const LabPage = lazy(() => import('./views/LabPage.jsx'), 'LabPage');
const Learn = lazy(() => import('./views/Learn.jsx'), 'Learn');
const Basics = lazy(() => import('./learn/lessons/Basics.jsx'), 'Basics');
const Settings = lazy(() => import('./views/Settings.jsx'), 'Settings');
const Info = lazy(() => import('./views/Info.jsx'), 'Info');
const Social = lazy(() => import('./views/social/index.jsx'), 'Social');
const Super = lazy(() => import('./views/Super.jsx'), 'Super');
const Collection = lazy(() => import('./views/collection/index.jsx'), 'Collection');
const Compete = lazy(() => import('./views/compete/index.jsx'), 'Compete');

// Routes: [pattern, component, props]. Social routes are served by one lazily loaded module.
const ROUTES = [
  ['/', Home],
  ['/play', PlayComputer],
  ['/lab', LabPage],
  ['/lab/:code', LabPage],
  ['/studio', LabPage],
  ['/studio/:code', LabPage],
  ['/learn', Learn],
  ['/learn/basics', Basics],
  ['/learn/basics/:id', Basics],
  ['/settings', Settings],
  ['/super', Super],
  ['/collection', Collection, { page: 'collection' }],
  ['/collection/item/:id', Collection, { page: 'item' }],
  ['/i/:id', Collection, { page: 'item' }],
  ['/odds', Collection, { page: 'odds' }],
  ['/loadout', Collection, { page: 'loadout' }],
  ['/caskets', Collection, { page: 'caskets' }],
  ['/shop', Collection, { page: 'shop' }],
  ['/market', Collection, { page: 'market' }],
  ['/trades', Collection, { page: 'trades' }],
  ['/compete', Compete, { page: 'hub' }],
  ['/quests', Compete, { page: 'quests' }],
  ['/achievements', Compete, { page: 'achievements' }],
  ['/season', Compete, { page: 'season' }],
  ['/ranked', Compete, { page: 'ranked' }],
  ['/events', Compete, { page: 'events' }],
  ['/events/:id', Compete, { page: 'event' }],
  ['/t/:id', Compete, { page: 'arena' }],
  ['/privacy', Info, { page: 'privacy' }],
  ['/credits', Info, { page: 'credits' }],
  ['/about', Info, { page: 'about' }],
  ['/new', Social, { page: 'new' }],
  ['/g/:id', Social, { page: 'game' }],
  ['/r/:id', Social, { page: 'replay' }],
  ['/s/:code', Social, { page: 'set' }],
  ['/friends', Social, { page: 'friends' }],
  ['/u/:handle', Social, { page: 'profile' }],
  ['/clubs', Social, { page: 'clubs' }],
  ['/c/:id', Social, { page: 'club' }],
  ['/f/:code', Social, { page: 'invite' }],
  ['/signin', Social, { page: 'signin' }],
  ['/me', Social, { page: 'me' }]
];

// Old addresses that now live elsewhere (the retired /sets gallery, the old /collection/settings page). The router
// resolves them before the first render.
setRedirects({ '/sets': '/collection', '/collection/settings': '/settings#collection' });

// Sections. Desktop shows them all in the top bar; phones show the `tab` ones in the tab bar (the `centre` one raised)
// and Learn as an icon in the top bar. See docs/DESIGN-SYSTEM.md, section 5. Set pages belong to the Collection (the
// old /sets gallery redirects there); the Lab (the old studio) is reached only from the super page.
const COLLECTION_PATHS = ['/loadout', '/caskets', '/shop', '/market', '/trades', '/odds'];
const COMPETE_PATHS = ['/compete', '/ranked', '/season', '/quests', '/achievements'];
// Your own profile is yours, not a friend's: it lights no section, like the account page.
const ownProfile = (p, me) => {
  if (!me || !me.handle || !p.startsWith('/u/')) return false;
  try { return decodeURIComponent(p.slice(3)).toLowerCase().replace(/^@/, '') === me.handle; } catch { return false; }
};
// A replay belongs with the games it came from: it lights Play at every width.
const NAV = [
  { href: '/', label: 'Home', icon: 'home', tab: 1, match: p => p === '/' },
  { href: '/play', label: 'Play', icon: 'knight', tab: 3, centre: true, match: p => p.startsWith('/play') || p.startsWith('/g/') || p.startsWith('/r/') || p === '/new' },
  { href: '/collection', label: 'Collection', icon: 'grid', tab: 2, match: p => p.startsWith('/collection') || p.startsWith('/i/') || p.startsWith('/s/') || COLLECTION_PATHS.includes(p) },
  { href: '/compete', label: 'Compete', icon: 'trophy', tab: 4, match: p => COMPETE_PATHS.includes(p) || p.startsWith('/events') || p.startsWith('/t/') },
  { href: '/learn', label: 'Learn', icon: 'learn', match: p => p.startsWith('/learn') },
  { href: '/friends', label: 'Friends', icon: 'friends', tab: 5, match: (p, me) => p.startsWith('/friends') || (p.startsWith('/u/') && !ownProfile(p, me)) || p.startsWith('/c/') || p === '/clubs' }
];
const TABS = NAV.filter(n => n.tab).sort((a, b) => a.tab - b.tab);
const fmtInt = n => Number(n || 0).toLocaleString('en-GB');

function BrandMark() {
  // The Heirloom hallmark: a lozenge struck with a crowned king (docs/DESIGN-SYSTEM.md, brand).
  return (
    <span class="brand-mark" aria-hidden="true">
      <svg viewBox="0 0 100 100"><path d="M50 5 95 50 50 95 5 50Z" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linejoin="miter" /><path fill="currentColor" d="M47.6 21h4.8v5h5v4.6h-5v5.4h-4.8v-5.4h-5V26h5zM37.5 47.5c1.5-7 6.5-10 12.5-10s11 3 12.5 10zM36.5 49.5h27v4h-27zM40.5 55.5h19l3.5 13.5H37zM36 71h28a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H36a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" /></svg>
    </span>
  );
}

export function App() {
  const route = useRoute();
  const { online, me, notifications, progress: progress0 } = useStore(s => ({ online: s.online, me: s.me, notifications: s.notifications, progress: s.progress }));
  // the player's face: the equipped king for accounts, the starter set's king for guests
  const { king } = useMyArmy();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(scrollY > 4);
    addEventListener('scroll', on, { passive: true });
    return () => removeEventListener('scroll', on);
  }, []);

  let View = null, params = {}, extra = {};
  for (const [pattern, comp, props] of ROUTES) {
    const m = match(pattern, route.path);
    if (m) { View = comp; params = m; extra = props || {}; break; }
  }
  const fullBleed = /^\/(play|g\/|r\/)/.test(route.path);

  useEffect(() => {
    const titles = { '/': 'Heirloom Chess', '/play': 'Play the computer', '/learn': 'Learn', '/friends': 'Friends', '/settings': 'Settings',
      '/collection': 'Collection', '/loadout': 'Loadout', '/caskets': 'Caskets', '/shop': 'Shop', '/market': 'Market', '/trades': 'Trades',
      '/odds': 'Odds', '/new': 'New game', '/about': 'About', '/privacy': 'Privacy', '/credits': 'Credits' };
    const t = titles[route.path];
    if (t) document.title = t === 'Heirloom Chess' ? t : `${t} · Heirloom Chess`;
  }, [route.path]);

  // The progress object from GET /api/me/progress, when the progression package puts it in the store.
  const progress = me && progress0 && typeof progress0.level === 'number' ? progress0 : null;
  const xpP = progress && progress.next ? Math.max(0, Math.min(1, (progress.into || 0) / progress.next)) : 0;
  const coins = progress && typeof progress.coins === 'number' ? (
    <a class="coins" href="/shop" title="Coins: earned by playing, never bought"><span class="coin" aria-hidden="true" /><span class="tabnum">{fmtInt(progress.coins)}</span><span class="visually-hidden"> coins</span></a>
  ) : null;
  const meFace = size => progress
    ? <span class="level-ring" style={{ '--p': xpP, '--size': size + 'px' }}><span class="level-face"><Avatar setCode={king} size={size - 14} /></span><b class="level-num">{progress.level}</b></span>
    : <Avatar setCode={king} size={size - 8} />;
  const meName = me && me.name ? me.name.split(' ')[0].slice(0, 12) : 'You';

  return (
    <div class="app">
      <a href="#main" class="visually-hidden" data-native onClick={e => { e.preventDefault(); document.getElementById('main')?.focus(); }}>Skip to content</a>
      <nav class="rail" aria-label="Main">
        <a class="brand" href="/" aria-label="Heirloom Chess home"><BrandMark /><span class="wordmark" aria-hidden="true">Heirloom <span class="wm-accent">Chess</span></span></a>
        {NAV.map(n => (
          <a key={n.href} class="nav" href={n.href} aria-current={n.match(route.path, me) ? 'page' : undefined}>
            <Icon name={n.icon} /><span class="lbl">{n.label}</span>
            {n.href === '/friends' && notifications ? <span class="badge">{notifications}</span> : null}
          </a>
        ))}
        <div class="rail-foot">
          {!online ? <span class="chip" title="Games against the computer, the coach and your drills all work offline"><Icon name="wifiOff" size={14} />Offline</span> : null}
          {coins}
          {me && me.super ? <a class="nav" href="/super" aria-current={route.path === '/super' || route.path.startsWith('/lab') ? 'page' : undefined}><Icon name="key" /><span class="lbl">Super</span></a> : null}
          <a class="icon-btn" href="/settings" aria-label="Settings" aria-current={route.path === '/settings' ? 'page' : undefined}><Icon name="settings" /></a>
          <a class="me-link" href="/me" aria-label="Your account">{meFace(44)}<span class="me-name">{meName}{progress ? <small>Level {progress.level}</small> : null}</span></a>
        </div>
        {progress ? <div class="xp-line" style={{ '--p': xpP }} aria-hidden="true" /> : null}
      </nav>
      <div style={{ minWidth: 0 }}>
        <header class={'topbar' + (scrolled ? ' scrolled' : '')}>
          <a class="brand" href="/"><BrandMark /><span class="wordmark">Heirloom <span class="wm-accent">Chess</span></span></a>
          {!online ? <span class="chip" title="Games against the computer, the coach and your drills all work offline"><Icon name="wifiOff" size={14} />Offline</span> : null}
          {coins}
          <a class="icon-btn" href="/learn" aria-label="Learn" aria-current={route.path.startsWith('/learn') ? 'page' : undefined}><Icon name="learn" /></a>
          <a class="icon-btn mobile-only" href="/settings" aria-label="Settings"><Icon name="settings" /></a>
          <a class="icon-btn" href="/me" aria-label="Your account">{meFace(38)}</a>
          {progress ? <div class="xp-line" style={{ '--p': xpP, position: 'absolute', left: 0, right: 0, bottom: '-1px' }} aria-hidden="true" /> : null}
        </header>
        <main id="main" tabIndex={-1} class={'app-main' + (fullBleed ? ' full-bleed' : '')} style={{ outline: 'none' }}>
          {View ? <View {...params} {...extra} query={route.query} key={route.path} /> : <NotFound />}
        </main>
      </div>
      <nav class="tabbar" aria-label="Main" style={{ '--tabs': TABS.length }}>
        {TABS.map(n => (
          <a key={n.href} href={n.href} class={n.centre ? 'centre' : undefined} aria-current={n.match(route.path, me) ? 'page' : undefined}>
            {n.centre ? <span class="tab-gem"><Icon name={n.icon} /></span> : <Icon name={n.icon} />}<span class="lbl">{n.label}</span>
            {n.href === '/friends' && notifications ? <span class="badge">{notifications}</span> : null}
          </a>
        ))}
      </nav>
      <Toasts />
    </div>
  );
}

function NotFound() {
  return (
    <div class="page narrow">
      <div class="empty">
        <span class="empty-mark" aria-hidden="true" />
        <p class="eyebrow">Not found</p>
        <h2>This square is empty</h2>
        <p>The page you were looking for is not here.</p>
        <a class="btn primary" href="/">Go home</a>
      </div>
    </div>
  );
}
